import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import { CommonService } from '../../core/services/common/common.service';
import { SnackbarService } from '../../core/services/snackbar/snackbar.service';
import {
  downloadResponseDocument,
  viewResponseDocument,
} from '../../core/utilities/utilities';
import { noAction } from '../actions/commonAction';
import {
  addDocument,
  addDocumentFailure,
  deleteDocument,
  documentsFailed,
  documentsReceived,
  downloadDocument,
  editDocument,
  getDocumentTypes,
  getDocumentTypesFailed,
  getDocumentTypesReceived,
  getDocuments,
  viewDocument,
} from '../actions/documentsTabActions';
import { getCityPermits, getInsurancePolicy } from '../actions/companyAction';
import { getOperatingAuthorityApplicationDocumentsData } from '../actions/operatingAuthorityApplicationAction';
import { OperatingAuthorityApplicationService } from '../../core/services/operatingAuthorityApplication/operating-authority-application.service';
import { getVehicles, submitForReviewPopup } from '../actions/vehicleActions';
@Injectable()
export class documentTabEffects {
  constructor(
    private actions: Actions,
    private snackbarService: SnackbarService,
    private commonService: CommonService,
    private operatingAuthorityApplicationService: OperatingAuthorityApplicationService,
  ) {}

  documentsRequest$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        getDocuments,
        getDocumentTypes,
        addDocument,
        deleteDocument,
        viewDocument,
        downloadDocument,
        editDocument,
      ),
      mergeMap((action) => {
        if (action.type === 'get-documents') {
          return this.commonService.getDocuments(action.params).pipe(
            map((data) => {
              return documentsReceived({ data: data });
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(documentsFailed());
            }),
          );
        } else if (action.type === 'get-documentTypes') {
          return this.commonService.getDocumentTypes(action.params).pipe(
            map((data) => {
              return getDocumentTypesReceived({ data: JSON.parse(data) });
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(getDocumentTypesFailed());
            }),
          );
        } else if (action.type === 'add-document') {
          return this.commonService.addDocument(action.params).pipe(
            mergeMap((data) => {
              this.snackbarService.success(
                action.params.referenceType === 'INS'
                  ? 'Company ACORD Certificate document has been uploaded successfully'
                  : action.params.referenceType === 'CPER'
                    ? 'Company City / State permit document has been uploaded successfully'
                    : action.params.referenceType === 'VEHC'
                      ? 'Vehicle Document has been uploaded successfully'
                      : 'Company Document has been uploaded successfully',
              );
              return action.params.referenceType === 'INS'
                ? of(
                    getInsurancePolicy({
                      permitId: action.permitId,
                    }),
                  )
                : action.params.referenceType === 'CPER'
                  ? of(
                      getCityPermits({
                        permitId: action.permitId,
                      }),
                    )
                  : action.isReadyForReview === true
                    ? of(
                        getVehicles({ params: action.permitId }),
                        submitForReviewPopup({ params: true }),
                      )
                    : action.displayType === 'OAA'
                      ? of(
                          getOperatingAuthorityApplicationDocumentsData({
                            params: {
                              sessionHash: action.params.sessionHash,
                              permitRequestId:
                                this.operatingAuthorityApplicationService
                                  .selectedOAAdata.requestId,
                            },
                          }),
                        )
                      : of(
                          getDocuments({
                            params: {
                              referenceType: action.params.referenceType,
                              referenceId: action.params.referenceId,
                              sessionHash: action.params.sessionHash,
                            },
                          }),
                        );
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(
                noAction(),
                addDocumentFailure(),
                submitForReviewPopup({ params: false }),
              );
            }),
          );
        } else if (action.type === 'view-document') {
          return this.commonService.viewDocument(action.params).pipe(
            map((data) => {
              viewResponseDocument(data);
              return noAction();
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(noAction());
            }),
          );
        } else if (action.type === 'delete-document') {
          return this.commonService.deleteDocument(action.params).pipe(
            map(() => {
              this.snackbarService.danger(
                action.params.referenceType === 'INS'
                  ? 'Company ACORD Certificate document has been deleted successfully'
                  : action.params.referenceType === 'CPER'
                    ? 'Company City / State permit document has been deleted successfully'
                    : action.params.referenceType === 'VEHC'
                      ? 'Vehicle Document has been deleted successfully'
                      : 'Company Document has been deleted successfully',
              );
              return action.params.referenceType === 'INS'
                ? getInsurancePolicy({
                    permitId: action.permitId,
                  })
                : action.params.referenceType === 'CPER'
                  ? getCityPermits({
                      permitId: action.permitId,
                    })
                  : action.displayType === 'OAA'
                    ? getOperatingAuthorityApplicationDocumentsData({
                        params: {
                          sessionHash: action.params.sessionHash,
                          permitRequestId:
                            this.operatingAuthorityApplicationService
                              .selectedOAAdata.requestId,
                        },
                      })
                    : getDocuments({ params: action.params });
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(getDocumentTypesFailed());
            }),
          );
        } else if (action.type === 'download-document') {
          return this.commonService.downloadDocument(action.params).pipe(
            map((data) => {
              this.snackbarService.success(
                'Document has been successfully downloaded',
              );
              downloadResponseDocument(data, action.params.fileName);
              return noAction();
            }),
            catchError((error) => {
              this.snackbarService.handleError(error);
              return of(noAction());
            }),
          );
        } else if (action.type === 'edit-document') {
          return this.commonService.editDocument(action.params).pipe(
            map((data) => {
              this.snackbarService.success(
                action.params.referenceType === 'INS'
                  ? 'Company ACORD Certificate document has been updated successfully'
                  : action.params.referenceType === 'CPER'
                    ? 'Company City / State permit document has been updated successfully'
                    : action.params.referenceType === 'VEHC'
                      ? 'Vehicle Document has been updated successfully'
                      : 'Company Document has been updated successfully',
              );
              return action.params.referenceType === 'INS'
                ? getInsurancePolicy({
                    permitId: action.permitId,
                  })
                : action.params.referenceType === 'CPER'
                  ? getCityPermits({
                      permitId: action.permitId,
                    })
                  : action.displayType === 'OAA'
                    ? getOperatingAuthorityApplicationDocumentsData({
                        params: {
                          sessionHash: action.params.sessionHash,
                          permitRequestId:
                            this.operatingAuthorityApplicationService
                              .selectedOAAdata.requestId,
                        },
                      })
                    : getDocuments({
                        params: {
                          referenceType: action.params.referenceType,
                          referenceId: action.params.referenceId,
                          sessionHash: action.params.sessionHash,
                        },
                      });
            }),
            catchError((error) => {
              this.snackbarService.handleError(error?.message);
              return of(getDocumentTypesFailed());
            }),
          );
        }
        return of(noAction());
      }),
    ),
  );
}
